import { Alert, Card, Spinner } from "flowbite-react";
import ReactApexChart from "react-apexcharts";
import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import { ADMIN } from "../../config/apiConfig";

const RESPONSE_TIME_SERIES_NAME = "Tempo médio (s)";

const getChartOptions = (categories = []) => ({
  chart: {
    type: "bar",
    toolbar: { show: false },
    animations: { enabled: true },
  },
  plotOptions: {
    bar: {
      borderRadius: 6,
      dataLabels: {
        position: "top",
      },
    },
  },
  dataLabels: {
    enabled: true,
    offsetY: -20,
    formatter: (val) => `${Number(val).toFixed(2)}s`,
    style: { fontSize: "12px", colors: ["#0f172a"] },
  },
  xaxis: {
    categories,
    labels: {
      rotate: -45,
      style: { fontSize: "11px" },
    },
  },
  yaxis: {
    labels: {
      formatter: (value) => `${Number(value).toFixed(2)}s`,
    },
    title: {
      text: "Segundos",
    },
  },
  tooltip: {
    y: {
      formatter: (value) => `${Number(value).toFixed(2)}s`,
    },
  },
  grid: {
    borderColor: "#e5e7eb",
  },
});

const getBarColor = (value) => {
  if (value > 30) return "#dc2626"; // red
  if (value > 10) return "#f59e0b"; // amber
  return "#2563eb"; // blue
};

const getChartLabel = (item) =>
  item.queryName ||
  (item.QueryId ? `Consulta #${item.QueryId}` : "Consulta modular");

const buildChartPayload = (rawData) => {
  const dataArray = Array.isArray(rawData) ? rawData : [];

  const chartData = dataArray
    .map((item) => {
      const avgExecutionTime = Number(item?.avgExecutionTime) || 0;
      const label = getChartLabel(item);
      return {
        ...item,
        avgExecutionTime,
        label,
      };
    })
    .sort((a, b) => b.avgExecutionTime - a.avgExecutionTime)
    .slice(0, 12);

  const categories = chartData.map((item) => item.label);

  const series = [
    {
      name: RESPONSE_TIME_SERIES_NAME,
      data: chartData.map((item) => ({
        x: item.label,
        y: item.avgExecutionTime,
        fillColor: getBarColor(item.avgExecutionTime),
      })),
    },
  ];

  return { chartData, categories, series };
};

const useResponseTimesQuery = (isModular, startDate, endDate) =>
  useQuery(
    ["responseTimes", isModular ? "modular" : "non-modular", startDate, endDate],
    async () => {
      const params = { isModular: isModular ? 1 : 0 };
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const { data } = await axios.get(ADMIN.GET_MODULAR_RESPONSE_TIMES, {
        params,
      });
      return data?.data ?? [];
    },
    {
      keepPreviousData: true,
    }
  );

const ModularResponseTimeChart = () => {
  const [pendingStartDate, setPendingStartDate] = useState("");
  const [pendingEndDate, setPendingEndDate] = useState("");
  const [activeStartDate, setActiveStartDate] = useState("");
  const [activeEndDate, setActiveEndDate] = useState("");

  const modularQuery = useResponseTimesQuery(
    true,
    activeStartDate,
    activeEndDate
  );
  const nonModularQuery = useResponseTimesQuery(
    false,
    activeStartDate,
    activeEndDate
  );

  const modularChart = useMemo(
    () => buildChartPayload(modularQuery.data),
    [modularQuery.data]
  );
  const nonModularChart = useMemo(
    () => buildChartPayload(nonModularQuery.data),
    [nonModularQuery.data]
  );

  const modularOptions = useMemo(
    () => getChartOptions(modularChart.categories),
    [modularChart.categories]
  );
  const nonModularOptions = useMemo(
    () => getChartOptions(nonModularChart.categories),
    [nonModularChart.categories]
  );

  const hasFilters = Boolean(activeStartDate || activeEndDate);
  const canApplyFilter = Boolean(
    pendingStartDate?.trim() || pendingEndDate?.trim()
  );

  const handleApplyFilter = () => {
    const normalizedStart = pendingStartDate?.trim() || "";
    const normalizedEnd = pendingEndDate?.trim() || "";

    if (
      normalizedStart === activeStartDate &&
      normalizedEnd === activeEndDate
    ) {
      modularQuery.refetch();
      nonModularQuery.refetch();
      return;
    }

    setActiveStartDate(normalizedStart);
    setActiveEndDate(normalizedEnd);
  };

  const deactivateFilter = () => {
    if (!hasFilters) return;
    setActiveStartDate("");
    setActiveEndDate("");
  };

  const resetFilterInputs = () => {
    setActiveStartDate("");
    setActiveEndDate("");
    setPendingStartDate("");
    setPendingEndDate("");
  };

  const renderChartSection = ({
    title,
    subtitle,
    queryState,
    chartInfo,
    options,
    emptyMessage,
  }) => {
    const chartIsEmpty = chartInfo.chartData.length === 0;

    return (
      <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <div className="mb-3">
          <p className="text-sm font-semibold text-gray-900">{title}</p>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        {queryState.isLoading ? (
          <div className="flex h-[280px] items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : queryState.error ? (
          <Alert color="failure">
            <span className="text-sm">
              Ocorreu um erro ao buscar os tempos de resposta.
            </span>
          </Alert>
        ) : chartIsEmpty ? (
          <p className="text-sm text-gray-500">{emptyMessage}</p>
        ) : (
          <div className="h-[320px]">
            <ReactApexChart
              options={options}
              series={chartInfo.series}
              type="bar"
              height={320}
            />
          </div>
        )}
      </section>
    );
  };

  return (
    <Card>
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              Tempo médio de resposta das consultas
            </h3>
            <span className="text-xs text-gray-500">
              baseado apenas em consultas com sucesso
            </span>
          </div>
          {hasFilters && (
            <button
              type="button"
              onClick={resetFilterInputs}
              className="text-xs text-blue-600 hover:underline"
            >
              Limpar filtro
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-sm text-gray-700">
          <label className="flex flex-col gap-1">
            <span className="font-medium">Data inicial</span>
            <input
              type="date"
              value={pendingStartDate}
              onChange={(event) => setPendingStartDate(event.target.value)}
              className="h-9 w-40 rounded border border-gray-300 px-3 text-sm outline-none focus:border-blue-500"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-medium">Data final</span>
            <input
              type="date"
              value={pendingEndDate}
              onChange={(event) => setPendingEndDate(event.target.value)}
              className="h-9 w-40 rounded border border-gray-300 px-3 text-sm outline-none focus:border-blue-500"
            />
          </label>
          <div className="flex flex-col justify-end gap-1">
            <span className="sr-only">
              Aplicar ou anular o filtro por data
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className={`h-9 rounded px-3 text-xs font-medium text-white transition ${
                  canApplyFilter
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-blue-200 cursor-not-allowed"
                }`}
                onClick={handleApplyFilter}
                disabled={!canApplyFilter}
              >
                Filtrar
              </button>
              <button
                type="button"
                className={`h-9 rounded border px-3 text-xs font-medium transition ${
                  hasFilters
                    ? "border-blue-600 text-blue-600 hover:bg-blue-50"
                    : "border-gray-200 text-gray-400 cursor-not-allowed"
                }`}
                onClick={deactivateFilter}
                disabled={!hasFilters}
              >
                Não filtrar
              </button>
            </div>
          </div>
        </div>
        <div className="grid gap-4">
          {renderChartSection({
            title: "Consultas modulares",
            subtitle: "Tempo médio de consultas modulares",
            queryState: modularQuery,
            chartInfo: modularChart,
            options: modularOptions,
            emptyMessage:
              "Ainda não existem consultas modulares com tempo registrado.",
          })}
          {renderChartSection({
            title: "Consultas não modulares",
            subtitle: "Tempo médio de consultas não modulares",
            queryState: nonModularQuery,
            chartInfo: nonModularChart,
            options: nonModularOptions,
            emptyMessage:
              "Ainda não existem consultas não modulares com tempo registrado.",
          })}
        </div>
      </div>
    </Card>
  );
};

export default ModularResponseTimeChart;
